import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Edit, Trash2, TrendingUp, ExternalLink, RefreshCw, Image, UserCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Tables } from "@/integrations/supabase/types";
import { EditPostDialog } from "./EditPostDialog";

type PostMedia = {
  id: string;
  media_url: string;
  media_type: string;
};

type Post = Tables<"posts"> & {
  post_analytics?: Tables<"post_analytics">[] | null;
  post_media?: PostMedia[];
  platform_post_url?: string | null;
  error_message?: string | null;
  retry_count?: number | null;
  assigned_to?: string | null;
  assigned_at?: string | null;
  org_id?: string | null;
};

const STATUS_BADGE: Record<string, { label: string; colour: string }> = {
  draft:     { label: "Draft",     colour: "bg-muted text-muted-foreground" },
  scheduled: { label: "Scheduled", colour: "bg-blue-100 text-blue-700" },
  published: { label: "Published", colour: "bg-[#E1F5EE] text-[#006B3C]" },
  failed:    { label: "Failed",    colour: "bg-red-100 text-red-700" },
  pending:   { label: "In Review", colour: "bg-amber-100 text-amber-700" },
  rejected:  { label: "Rejected",  colour: "bg-red-100 text-red-700" },
};

export const PostsList = () => {
  const { orgId, can } = useOrgPermissions();
  const canManageAll = can("social_media", "edit");
  const queryClient = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "mine" | "assigned" | "scheduled">("all");
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id ?? null);
    });
  }, []);

  const { data: posts = [], isLoading: loading } = useQuery({
    queryKey: ["org-posts", orgId, canManageAll],
    enabled: !!orgId && !!currentUserId,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from("posts")
        .select(`
          *,
          post_analytics (*),
          post_media (id, media_url, media_type)
        `)
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });

      if (!canManageAll) {
        query = (query as any).or(`user_id.eq.${user.id},assigned_to.eq.${user.id}`);
      }

      const { data } = await query;
      return (data || []) as unknown as Post[];
    },
  });

  const assigneeIds = useMemo(() => {
    const ids = [...new Set(posts.map((p: any) => p.assigned_to).filter(Boolean))];
    return ids as string[];
  }, [posts]);

  const { data: assigneeProfiles } = useQuery({
    queryKey: ["assignee-profiles", assigneeIds],
    enabled: assigneeIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", assigneeIds);
      return Object.fromEntries((data || []).map((p: any) => [p.user_id, p.full_name])) as Record<string, string>;
    },
  });

  const assignedCount = useMemo(
    () => posts.filter((p: any) => p.assigned_to === currentUserId).length,
    [posts, currentUserId]
  );

  const filteredPosts = useMemo(() => {
    switch (filter) {
      case "mine":
        return posts.filter((p: any) => p.user_id === currentUserId);
      case "assigned":
        return posts.filter((p: any) => p.assigned_to === currentUserId);
      case "scheduled":
        return posts.filter((p: any) => p.status === "scheduled" && p.scheduled_at);
      default:
        return posts;
    }
  }, [posts, filter, currentUserId]);

  // Realtime: org-scoped, invalidates the query
  useEffect(() => {
    if (!orgId) return;
    const channel = supabase
      .channel(`posts-${orgId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "posts",
        filter: `org_id=eq.${orgId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["org-posts", orgId] });
      })
      .subscribe();

    channelRef.current = channel;
    return () => { channel.unsubscribe(); };
  }, [orgId, queryClient]);

  const handleDelete = async (postId: string) => {
    try {
      const { error } = await supabase.from("posts").delete().eq("id", postId);
      if (error) throw error;
      toast({ title: "Post deleted", description: "Post has been removed" });
      queryClient.invalidateQueries({ queryKey: ["org-posts", orgId] });
    } catch {
      toast({ title: "Error", description: "Failed to delete post", variant: "destructive" });
    }
  };

  const handleRetry = async (postId: string) => {
    try {
      const post = posts.find((p: any) => p.id === postId);
      if (!post || !(post as any).social_account_id) return;
      await supabase.from("posts").update({
        status: "published",
        error_message: null,
        retry_count: ((post as any).retry_count || 0) + 1,
      }).eq("id", postId);
      toast({ title: "Retrying...", description: "Attempting to publish post again" });
      queryClient.invalidateQueries({ queryKey: ["org-posts", orgId] });
    } catch {
      toast({ title: "Error", description: "Failed to retry post", variant: "destructive" });
    }
  };

  if (loading) return <Card className="p-6">Loading posts...</Card>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Posts</h2>
        <p className="text-justify text-muted-foreground">Manage your organisation's social media content</p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 flex-wrap">
        {([
          { key: "all",       label: "All Posts" },
          { key: "mine",      label: "My Posts" },
          { key: "assigned",  label: "Assigned to Me" },
          { key: "scheduled", label: "Scheduled" },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors flex items-center gap-1.5 ${
              filter === tab.key
                ? "bg-[#0D2240] text-white"
                : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {tab.label}
            {tab.key === "assigned" && assignedCount > 0 && (
              <span className="text-[10px] bg-[#C8A84B] text-[#0D2240] px-1.5 py-0.5 rounded-full font-bold leading-none">
                {assignedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-justify text-muted-foreground">No posts found</p>
          </Card>
        ) : (
          filteredPosts.map((post: any) => {
            const badge = STATUS_BADGE[post.status] ?? {
              label: post.status,
              colour: "bg-muted text-muted-foreground",
            };
            return (
              <Card key={post.id} className="p-6 hover:shadow-md transition-base">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.colour}`}>
                        {badge.label}
                      </span>
                      {post.is_ai_generated && (
                        <Badge variant="outline" className="text-xs">AI Generated</Badge>
                      )}
                      {post.platform && (
                        <Badge variant="outline" className="text-xs capitalize">{post.platform}</Badge>
                      )}
                    </div>

                    <p className="text-justify text-sm text-muted-foreground mb-3">
                      {post.scheduled_for
                        ? `Scheduled for ${new Date(post.scheduled_for).toLocaleString()}`
                        : `Created ${new Date(post.created_at!).toLocaleDateString()}`}
                    </p>

                    {post.assigned_to && (
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-2">
                        <UserCircle size={12} />
                        <span>
                          Assigned to {assigneeProfiles?.[post.assigned_to] || "Team member"}
                        </span>
                      </div>
                    )}

                    <p className="text-justify text-foreground whitespace-pre-wrap">{post.content}</p>

                    {post.post_media && post.post_media.length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {post.post_media.slice(0, 4).map((media: PostMedia) => (
                          <div key={media.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border">
                            {media.media_type === "image" ? (
                              <img src={media.media_url} alt="Post media" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <Image className="w-8 h-8 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        ))}
                        {post.post_media.length > 4 && (
                          <div className="w-20 h-20 rounded-lg border border-border flex items-center justify-center bg-muted">
                            <span className="text-sm text-muted-foreground">+{post.post_media.length - 4}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {post.platform_post_url && (
                      <a
                        href={post.platform_post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        View on platform <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    {post.error_message && (
                      <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
                        {post.error_message}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {post.status === "failed" && (
                      <Button size="sm" variant="outline" onClick={() => handleRetry(post.id)}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { setEditingPost(post); setEditDialogOpen(true); }}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(post.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {post.post_analytics && post.post_analytics[0] && (
                  <div className="flex items-center gap-6 pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4 text-success" />
                      <span className="text-muted-foreground">Likes:</span>
                      <span className="font-medium">{post.post_analytics[0].likes_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Comments:</span>
                      <span className="font-medium">{post.post_analytics[0].comments_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Shares:</span>
                      <span className="font-medium">{post.post_analytics[0].shares_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Engagement:</span>
                      <span className="font-medium text-success">
                        {post.post_analytics[0].engagement_rate?.toFixed(1) || 0}%
                      </span>
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      <EditPostDialog
        post={editingPost}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["org-posts", orgId] })}
      />
    </div>
  );
};
